import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MessageSquare, Clock, User, Building2, RotateCw, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const RISK_COLORS = {
  emergencial: "bg-red-100 text-red-800 border-red-200",
  alta: "bg-orange-100 text-orange-800 border-orange-200",
  moderada: "bg-amber-100 text-amber-800 border-amber-200",
  programado: "bg-slate-100 text-slate-700 border-slate-200",
};

const relTime = (iso) => {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return `${Math.max(1, Math.floor(diff / 60000))}min atrás`;
    if (h < 48) return `${h}h atrás`;
    return `${Math.floor(h / 24)}d atrás`;
  } catch { return "—"; }
};

const ActiveAlertsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/active-alerts");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar alertas");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data && loading) return <div className="p-8 text-slate-500 text-sm">Carregando alertas ativos…</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="active-alerts-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Alertas Ativos · Chegada +24h</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Reservas confirmadas há mais de 24h mas a clínica ainda não registrou a chegada.
            Pode ter havido no-show ou falha de comunicação — contate a unidade pelo WhatsApp abaixo.
          </p>
        </div>
        <button onClick={load} data-testid="active-alerts-refresh"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RotateCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {data.total === 0 ? (
        <div className="p-8 border border-dashed border-emerald-300 rounded-xl bg-emerald-50 text-emerald-800 text-sm text-center" data-testid="active-alerts-empty">
          Nenhum alerta ativo — todas as reservas recentes estão dentro do prazo de 24h.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <div className="col-span-3">Caso</div>
            <div className="col-span-2">Risco</div>
            <div className="col-span-2">Unidade</div>
            <div className="col-span-2">Consultor</div>
            <div className="col-span-1">Alerta</div>
            <div className="col-span-2 text-right">Ação</div>
          </div>
          {data.cases.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 items-center text-sm" data-testid={`alert-row-${c.protocol}`}>
              <div className="col-span-3">
                <div className="font-semibold text-[#0B2B62]">{c.protocol}</div>
                <div className="text-slate-700 truncate">{c.patient_name}</div>
                <div className="text-[11px] text-slate-500">{c.city}{c.state ? ` / ${c.state}` : ""}</div>
              </div>
              <div className="col-span-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-semibold ${RISK_COLORS[c.risk_level] || RISK_COLORS.programado}`}>
                  {c.risk_level || "—"}
                </span>
              </div>
              <div className="col-span-2 text-slate-700 truncate">
                <Building2 className="w-3.5 h-3.5 inline mr-1 text-slate-400" />{c.clinic_name || "—"}
              </div>
              <div className="col-span-2 text-slate-600 truncate text-xs">
                <User className="w-3.5 h-3.5 inline mr-1 text-slate-400" />{c.consultant_name || "—"}
              </div>
              <div className="col-span-1 text-slate-600 text-[11px]">
                <Clock className="w-3.5 h-3.5 inline mr-1 text-amber-500" />{relTime(c.arrival_alert_at)}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1.5">
                {c.whatsapp_link ? (
                  <a href={c.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid={`alert-whatsapp-${c.protocol}`}
                    className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 inline-flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                ) : (
                  <span className="text-[10px] text-slate-400 italic">sem telefone</span>
                )}
                <a href={`/admin/casos/${c.id}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs px-2 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50"
                  title="Abrir caso">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveAlertsPanel;
